import { Link, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Trophy, CheckCircle2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const statusStyles = {
  live: "bg-jpa-live text-white",
  running: "bg-jpa-live text-white",
  upcoming: "bg-jpa-accent text-jpa-ink",
  past: "bg-jpa-ink text-white",
};

export default function TournamentCard({ t }) {
  const label = t.status === "running" ? "LIVE NOW" : t.status.toUpperCase();
  const { user } = useAuth();
  const nav = useNavigate();
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) api.get("/me/entries").then((r) => setEntries(r.data)).catch(() => {});
  }, [user]);

  const registered = entries.some((e) => e.tournament_id === t.id);
  const canRegister = (t.status === "upcoming" || t.status === "running") && t.registration_open !== false;
  const externalUrl = t.external_booking_url || null;

  const doRegister = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
      return;
    }
    if (t.quick_registration) {
      nav(`/tournaments/${t.id}/quick-join`);
      return;
    }
    if (!user) { toast.info("Please log in to register"); nav("/login"); return; }
    setBusy(true);
    try {
      await api.post(`/tournaments/${t.id}/register`, {});
      toast.success("Entry submitted — pending admin approval");
      const r = await api.get("/me/entries");
      setEntries(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid={`tournament-card-${t.id}`}
      className="group block bg-white border border-jpa-border rounded-sm overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-transform duration-300 ease-out"
    >
      <Link to={`/tournaments/${t.id}`} className="block">
        <div className="relative aspect-[2/3] overflow-hidden bg-jpa-surface">
          {t.image_url && (
            <img src={t.image_url} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          <span className={`absolute top-3 left-3 px-2 py-1 text-[10px] font-bold uppercase tracking-widest skew-badge ${statusStyles[t.status] || statusStyles.upcoming} ${t.status === "running" ? "live-pulse" : ""}`}>
            {label}
          </span>
          {t.division && (
            <span className="absolute top-3 right-3 bg-white/90 backdrop-blur text-jpa-ink px-2 py-1 text-[10px] font-bold uppercase tracking-widest skew-badge">
              {t.division}
            </span>
          )}
          {/* JPA sanction badge — shown only for ranking-eligible divisions per user rule */}
          {["state", "district", "city"].includes(t.division) && t.approval_status && (
            <span
              className={`absolute top-11 right-3 px-2 py-1 text-[10px] font-bold uppercase tracking-widest skew-badge ${
                t.jpa_sanctioned
                  ? "bg-emerald-500 text-white"
                  : t.approval_status === "pending" ? "bg-amber-400 text-jpa-ink" : "bg-slate-500 text-white"
              }`}
              data-testid={`sanction-badge-${t.id}`}
            >
              {t.jpa_sanctioned ? "JPA SANCTIONED" : t.approval_status === "pending" ? "AWAITING JPA" : "NOT SANCTIONED"}
            </span>
          )}
          {t.prize_pool && (
            <div className="absolute bottom-3 right-3 bg-jpa-ink/90 backdrop-blur text-jpa-accent px-3 py-1 text-xs font-bold uppercase tracking-wider">
              {t.prize_pool}
            </div>
          )}
        </div>
        <div className="p-5">
          <h3 className="font-display text-2xl tracking-wider text-jpa-ink group-hover:text-jpa-primary transition-colors line-clamp-2">{t.name}</h3>
          <div className="mt-3 space-y-1.5 text-sm text-jpa-muted">
            <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-jpa-primary flex-shrink-0" /> {t.location}</div>
            <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-jpa-primary flex-shrink-0" /> {t.start_date} → {t.end_date}</div>
            <div className="flex items-center gap-2"><Trophy className="w-4 h-4 text-jpa-primary flex-shrink-0" /> {t.category}</div>
          </div>
        </div>
      </Link>
      {canRegister && (
        <div className="px-5 pb-5">
          {registered ? (
            <div data-testid={`card-registered-${t.id}`} className="flex items-center justify-center gap-2 py-2.5 border border-green-300 bg-green-50 text-green-800 text-xs font-bold uppercase tracking-widest rounded-sm">
              <CheckCircle2 className="w-4 h-4" /> Registered
            </div>
          ) : (
            <button
              onClick={doRegister}
              disabled={busy}
              data-testid={`card-register-${t.id}`}
              className="w-full py-2.5 bg-jpa-primary hover:bg-jpa-primary-hover disabled:opacity-50 text-white text-xs font-bold uppercase tracking-widest rounded-sm transition-colors"
            >
              {busy ? "Submitting..." : externalUrl ? "Book externally ↗" : t.quick_registration ? "Join now →" : "Register now"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
